import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { menuRowClass } from "@/components/ui/menu-surface";
import { LOCALES } from "@/engine/support/locales";
import { setLocale } from "@/engine/state/locale";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/engine/support/classNames";
import { SettingsSelectRow } from "./SettingsGroup";

/**
 * The language control, on the shared menu surface beside the theme row.
 *
 * A select rather than a row per language: two locales would fit as rows, and
 * the ~50 that #254 brings would not. Each is named in its own language,
 * because a visitor looking for their language is looking for the word they
 * would write, not the English one.
 */
export function LanguageMenuRow({
  className,
  variant = "menu",
}: {
  className?: string;
  /**
   * `menu`: a row of the dropdown, with the select at its end. `row`: the
   * whole row opens the list, for a settings card in a narrow column.
   * `compact`: just the select, icon inside, for a header.
   */
  variant?: "menu" | "row" | "compact";
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const options = LOCALES.map((option) => (
    <SelectItem
      key={option.code}
      value={option.code}
      lang={option.code}
      dir={option.direction}
    >
      {option.nativeName}
    </SelectItem>
  ));

  if (variant === "row") {
    const current = LOCALES.find((option) => option.code === locale);
    return (
      <SettingsSelectRow
        menuAction
        data-testid="language-select"
        aria-label={t("settings.chooseLanguage")}
        icon={<Languages />}
        label={t("settings.language")}
        chosen={current?.nativeName ?? locale}
        value={locale}
        onValueChange={setLocale}
      >
        {options}
      </SettingsSelectRow>
    );
  }

  if (variant === "compact") {
    return (
      <Select value={locale} onValueChange={setLocale}>
        <SelectTrigger
          data-testid="language-select"
          aria-label={t("settings.chooseLanguage")}
          className={cn("h-9 w-auto gap-2 text-sm", className)}
        >
          <Languages
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{options}</SelectContent>
      </Select>
    );
  }

  return (
    <div className={cn(menuRowClass, "cursor-default", className)}>
      <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center text-muted-foreground">
        <Languages className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 text-left">{t("settings.language")}</span>
      <Select value={locale} onValueChange={setLocale}>
        <SelectTrigger
          data-menu-action
          data-testid="language-select"
          aria-label={t("settings.chooseLanguage")}
          className="h-8 w-auto min-w-28 text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{options}</SelectContent>
      </Select>
    </div>
  );
}
